import { createStore } from "zustand/vanilla";
import { loadStyleLink, applyStyle, setAppStyle } from "@/ui/style";
import { fetchURLAPI } from "@/ui/http";
import { getUrlParams } from "@/ui/url";
import gameManager from "@/ui/gameManager";
import { getKeyValueSet, prepareAssets } from "@/ui/assets";
import { gameRepo } from "./game";
import { preloadRepo } from "./preload";

type TParams = Record<string, any>;
type TConfigData = Record<string, any>;

export interface IConfigState {
  parsedAssets: Record<string, Record<string, string>>;
  configData: null | TConfigData;
  urlParams: Partial<TParams>;
  isUIActive: boolean;
  showUI: boolean;
  showUIdelay: number;
  showUIduration: number;
  isMobile?: boolean;
  isFullscreen: boolean;
  canFullscreen: boolean;
}
export interface IConfigRepo extends IConfigState {
  changeUIActive: (isUIActive: boolean) => void;
  load: () => void;
  loadAssets: () => void;
  getParsedAsset: (
    asset: string,
    type?: "assets" | "fonts_assets" | "sounds"
  ) => string;
  toggleFullscreen: () => void;
}

export const configRepo = createStore<IConfigRepo>((set, get) => {
  const urlParams = getUrlParams();

  const isMobile = (): boolean => {
    const mobile =
      window.matchMedia("(hover: none)").matches ||
      !!navigator.userAgent.match(/Android|iPhone|iPad|iPod/i)?.length;

    const body = document.querySelector("body");

    body?.classList.remove("is-mobile", "is-desktop");
    body?.classList.add(mobile ? "is-mobile" : "is-desktop");

    return mobile;
  };

  addEventListener("fullscreenchange", () =>
    set({
      isFullscreen: !!document.fullscreenElement,
    })
  );

  window.addEventListener("resize", () => {
    set({
      isMobile: isMobile(),
    });
  });

  return {
    parsedAssets: {
      assets: {},
      fonts_assets: {},
      sounds: {},
    },
    configData: null,
    urlParams,
    isUIActive: false,
    showUI: false,
    showUIdelay: 0,
    showUIduration: 0,
    isMobile: isMobile(),
    isFullscreen: !!document.fullscreenElement,
    canFullscreen: !!document.fullscreenEnabled,
    toggleFullscreen: () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
      } else if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    },
    changeUIActive: (isUIActive) => set({ isUIActive }),
    load: () => {
      preloadRepo.getState().setLoading("gameFiles");
      gameManager.loadScript(urlParams.game_url);

      fetchURLAPI(urlParams.config).then((configData) => {
        set({ configData });
      });
    },

    loadAssets: () => {
      const { base_assets_url, assets, fonts_assets, sounds, bonus_buy } =
        get().configData || {};
      const gameState = gameRepo.getState();
      const { casino_token, bonus_buy_enabled } = gameState;
      const { setProgress, setLoading, finish } = preloadRepo.getState();

      if (bonus_buy_enabled && bonus_buy) {
        gameState.injectBonusBuyData(bonus_buy, base_assets_url);
      }

      set({
        parsedAssets: {
          assets: prepareAssets(
            getKeyValueSet(assets, casino_token),
            base_assets_url
          ),
          fonts_assets: prepareAssets(
            getKeyValueSet(fonts_assets, casino_token),
            base_assets_url
          ),
          sounds: prepareAssets(
            getKeyValueSet(sounds, casino_token),
            base_assets_url
          ),
        },
      });

      setProgress("assets", 100);
      finish("assets");
    },

    getParsedAsset: (asset = "", type = "assets") => {
      return get().parsedAssets[type][asset] || "";
    },
  };
});

configRepo.subscribe(({ parsedAssets }, prev) => {
  if (parsedAssets === prev.parsedAssets) return;

  if (parsedAssets.fonts_assets) {
    Object.entries(parsedAssets.fonts_assets).map(([id, src]) => {
      loadStyleLink(`config_style_link__${id}`, src);
    });
  }
});

configRepo.subscribe(({ configData }, prev) => {
  if (!configData || configData === prev.configData) return;
  const { casino_token } = gameRepo.getState();

  const { colors, fonts, app_background_color, app_background_image } =
    configData;

  const colorsVars = getKeyValueSet(colors, casino_token);
  const fontsVars = getKeyValueSet(fonts, casino_token);

  if (app_background_color || app_background_image) {
    setAppStyle({
      color: app_background_color,
      image: app_background_image,
    });
  }

  applyStyle("config_style_vars__", {
    vars: {
      ...colorsVars,
      ...fontsVars,
    },
  });
});

configRepo.subscribe(({ isUIActive }, p) => {
  if (p.isUIActive !== isUIActive) {
    gameRepo.getState().send("menu_state", { value: isUIActive });
  }
});
